import { useDndContext, useDroppable } from '@dnd-kit/core';
import { type TFormField } from '@nirmata/shared';
import { CopyIcon, EditIcon, TrashIcon } from 'lucide-react';
import { useMemo } from 'react';

import { useBuilderStore } from '@/store/builder';
import { cn } from '@/utils/cn';

import { FORM_FIELDS_CONFIG } from './fields.config';

export function FormCanvas() {
  const { schema } = useBuilderStore();

  return (
    <div className="flex flex-col h-full">
      <div className="space-y-2 mb-4">
        <h2 className="text-base font-medium tracking-tight">Form Structure</h2>
        <p className="text-sm text-[var(--muted-foreground)]">
          Build your form by dragging fields from the library
        </p>
      </div>
      <div className="form-canvas-container flex-1">
        <FormDropArea schema={schema} />
      </div>
    </div>
  );
}

function FormDropArea({ schema }: { schema: TFormField | null }) {
  const { setNodeRef } = useDroppable({
    id: 'form-canvas',
  });

  return (
    <div
      ref={setNodeRef}
      data-droppable="true"
      className={cn(
        'relative rounded-lg border border-[var(--border)] p-4 overflow-y-auto transition-all h-full',
        'bg-[var(--popover)]',
        'before:content-[""] before:absolute before:inset-0 before:bg-[length:16px_16px] before:opacity-30 before:z-0',
        'before:bg-[radial-gradient(circle_at_1px_1px,var(--muted-foreground)_1px,transparent_0)]',
        'dark:before:opacity-20'
      )}
    >
      <div className="relative z-10">
        {!schema ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-[var(--muted-foreground)]">
              Drag and drop fields here to start building your form
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <RenderFormField field={schema} />
          </div>
        )}
      </div>
    </div>
  );
}

function RenderFormField({ field }: { field: TFormField }) {
  const { schema, selectedField, setSelectedField, removeField, addField } = useBuilderStore();
  const { over } = useDndContext();
  const isSelected = selectedField === field.fieldId;
  const canNest = field.childFields !== undefined;
  const isRoot = field.fieldId === schema?.fieldId;

  // Create drop zone IDs
  const aboveDropId = `drop-above-${field.fieldId}`;
  const insideDropId = `drop-inside-${field.fieldId}`;
  const belowDropId = `drop-below-${field.fieldId}`;

  // Get droppable states for this field
  const { setNodeRef: setAboveRef, isOver: isOverAbove } = useDroppable({
    id: aboveDropId,
    data: { target: 'above', fieldId: field.fieldId },
    disabled: isRoot,
  });

  const { setNodeRef: setInsideRef, isOver: isOverInside } = useDroppable({
    id: insideDropId,
    data: { target: 'inside', fieldId: field.fieldId },
    disabled: !canNest,
  });

  const { setNodeRef: setBelowRef, isOver: isOverBelow } = useDroppable({
    id: belowDropId,
    data: { target: 'below', fieldId: field.fieldId },
    disabled: isRoot,
  });

  // Determine if the parent container should be highlighted/elevated
  const isDraggingOverChild = useMemo(() => {
    if (!over || !field.childFields) return false;
    const overId = over.id as string;

    // Check if dragging over an above/below drop zone of a direct child
    return field.childFields.some(
      (child) =>
        overId === `drop-above-${child.fieldId}` || overId === `drop-below-${child.fieldId}`
    );
  }, [over, field.childFields]);

  // Parent highlight is active if dragging inside OR over a direct child's drop zone
  const isParentHighlightActive = isOverInside || isDraggingOverChild;

  // Get the field config to access the icon
  const fieldConfig = Object.values(FORM_FIELDS_CONFIG)
    .flat()
    .find((config) => config.defaultProps.componentType === field.componentType);

  const Icon = fieldConfig?.icon;

  return (
    <div className="relative mb-6">
      {/* Above drop zone - Do not render for root */}
      {!isRoot && (
        <div
          ref={setAboveRef}
          className={cn('absolute top-0 left-0 right-0 h-3 -mt-3 z-10 rounded-t-md transition-all')}
        >
          {isOverAbove && (
            <div className="border-t-2 border-dashed border-[var(--muted-foreground)] w-full absolute top-1/2 left-0 opacity-40" />
          )}
        </div>
      )}

      <div
        className={cn(
          'rounded-md border transition-all duration-200 shadow-sm hover:shadow overflow-hidden',
          'bg-[var(--background)] dark:bg-[var(--popover)]',
          isSelected
            ? 'border-[var(--primary)] bg-[var(--popover)]'
            : 'border-[var(--border)] hover:border-[var(--border)]',
          isParentHighlightActive &&
            canNest &&
            'shadow-lg border-[var(--primary)] transform translate-y-[-2px]',
          'relative z-0'
        )}
      >
        <div className="flex items-center justify-between px-4 py-2 bg-[var(--background)] dark:bg-[var(--popover)]">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-[var(--primary)] shrink-0" />}
            <div>
              {field.componentProps?.label ? (
                <p className="font-medium">{field.componentProps?.label as string}</p>
              ) : (
                <p className="text-[var(--foreground)]">{field.fieldName}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="p-1 rounded hover:bg-[var(--primary)]/10"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedField(field.fieldId);
              }}
              title="Edit"
            >
              <EditIcon className="w-4 h-4 text-[var(--primary)] cursor-pointer" />
            </button>
            {!isRoot && (
              <>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-[var(--primary)]/10"
                  onClick={(e) => {
                    e.stopPropagation();

                    // Recursive function to clone a field and all its nested children
                    const cloneField = (fieldToClone: TFormField): TFormField => {
                      // Get the base field ID without any timestamps (split by underscore and take the first part)
                      const baseFieldId = fieldToClone.fieldId.split('_')[0];
                      const newFieldId = `${baseFieldId}_${Date.now()}`;
                      const clonedField: TFormField = {
                        ...fieldToClone,
                        fieldId: newFieldId,
                        fieldName: `${fieldToClone.fieldName}_copy`,
                      };

                      // If the field has childFields, recursively clone them
                      if (fieldToClone.childFields?.length) {
                        clonedField.childFields = fieldToClone.childFields.map((child) =>
                          cloneField(child)
                        );
                      }

                      return clonedField;
                    };

                    const clonedField = cloneField(field);
                    addField(clonedField, field.fieldId, 'below');
                  }}
                  title="Clone"
                >
                  <CopyIcon className="w-4 h-4 text-[var(--primary)] cursor-pointer" />
                </button>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-[var(--destructive)]/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeField(field.fieldId);
                    if (selectedField === field.fieldId) setSelectedField(null);
                  }}
                  title="Delete"
                >
                  <TrashIcon className="w-4 h-4 text-[var(--destructive)] cursor-pointer" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Inside drop zone - only rendered for fields that can have children */}
        {canNest && (
          <div
            ref={setInsideRef}
            className={cn(
              'relative m-2 mt-0 min-h-24 rounded-md p-4 pb-8 transition-all duration-200 text-[var(--muted-foreground)]',
              'bg-[var(--background)] dark:bg-[var(--popover)]',
              isParentHighlightActive && 'shadow-lg shadow-[var(--primary)]',
              isOverInside && '!bg-[var(--popover)]',
              'overflow-hidden'
            )}
            style={{
              backgroundClip: 'padding-box',
            }}
          >
            {field.childFields && field.childFields.length > 0 ? (
              <div className="space-y-6 relative">
                {field.childFields.map((childField) => (
                  <RenderFormField key={childField.fieldId} field={childField} />
                ))}

                <div className="h-10 relative">
                  {isOverInside && (
                    <div className="absolute inset-x-4 top-1/2 transform -translate-y-1/2 flex items-center justify-center pointer-events-none">
                      <div className="w-full h-8 flex items-center justify-center bg-[var(--background)] dark:bg-[var(--popover)] border border-[var(--border)] rounded-md">
                        <span className="text-xs font-medium text-[var(--primary)]">
                          Drop at end
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-20 text-sm rounded-md w-full bg-transparent">
                {isParentHighlightActive ? ' ' : 'Drop fields here'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Below drop zone - Do not render for root */}
      {!isRoot && (
        <div
          ref={setBelowRef}
          className={cn(
            'absolute bottom-0 left-0 right-0 h-3 -mb-3 z-10 rounded-b-md transition-all'
          )}
        >
          {isOverBelow && (
            <div className="border-t-2 border-dashed border-[var(--muted-foreground)] w-full absolute bottom-1/2 left-0 opacity-40" />
          )}
        </div>
      )}
    </div>
  );
}
